import {sendAxiosRequestWithRetry} from "../services/sendAxiosRequestWithRetry";
import {EscPosCompiler, PrinterConfig, ReceiptBuilder, ThermalPrinter, WebSerialPort, WebUSBPort} from "web-thermal-printer-engine";

type TranslationFn = (key: string) => string;
type PrintCheckoutReceiptParams = {
    printMethod: Exclude<PrintMethod, "browser">;
    checkoutId?: CashBoxId | null;
    printPayload?: CashBoxReceiptPrintPayload | null;
    allowBluetoothDeviceSelection?: boolean;
    allowUsbDeviceSelection?: boolean;
};
type CheckoutPrintMessageParams = {
    error?: unknown;
    t: TranslationFn;
};
type CheckoutPrintableErrorLike = (ApiError | CashBoxPrintableError) & {
    name?: string;
    code?: string | number;
};

const isCheckoutPrintableErrorLike = (error: unknown): error is CheckoutPrintableErrorLike => (
    typeof error === "object" && error !== null
);

const getPrintableErrorCode = (error?: CheckoutPrintableErrorLike | null) => (
    typeof error?.code === "string"
        ? error.code
        : undefined
);

export const getCheckoutPrintErrorMessage = ({error, t}: CheckoutPrintMessageParams) => {
    const printableError = isCheckoutPrintableErrorLike(error) ? error : null;
    const printableErrorCode = getPrintableErrorCode(printableError);

    if (!error) {
        return t("unexpectedErrorOccurred");
    }

    if (printableErrorCode === "BLUETOOTH_UNAVAILABLE") {
        return t("bluetoothUnavailable");
    }

    if (printableErrorCode === "USB_UNAVAILABLE") {
        return t("usbUnavailable");
    }

    if (printableErrorCode === "BLUETOOTH_SECURE_CONTEXT_REQUIRED") {
        return t("bluetoothSecureContextRequired");
    }

    if (printableErrorCode === "USB_SECURE_CONTEXT_REQUIRED") {
        return t("usbSecureContextRequired");
    }

    if (printableErrorCode === "BLUETOOTH_DEVICE_SELECTION_REQUIRED") {
        return t("bluetoothAutoPrintRequiresSelection");
    }

    if (printableErrorCode === "USB_DEVICE_SELECTION_REQUIRED") {
        return t("usbAutoPrintRequiresSelection");
    }

    if (printableErrorCode === "USB_ACCESS_DENIED") {
        return t("usbAccessDenied");
    }

    if (printableError?.name === "NotFoundError") {
        if (printableErrorCode?.startsWith("USB_")) {
            return t("noCompatibleUsbPrinterFound");
        }

        return t("noCompatibleBluetoothPrinterFound");
    }

    if (
        printableError?.code === "USB_PRINTER_NOT_SUPPORTED" ||
        printableError?.name === "NotSupportedError" ||
        printableError?.name === "SecurityError" ||
        printableError?.name === "TypeError"
    ) {
        if (printableErrorCode?.startsWith("USB_")) {
            return t("usbPrintingNotSupported");
        }

        return t("bluetoothPrintingNotSupported");
    }

    return (
        ((printableError?.details?.errors && typeof printableError.details.errors === "object" && !Array.isArray(printableError.details.errors))
            ? ((printableError.details.errors as Record<string, unknown>).error || (printableError.details.errors as Record<string, unknown>).message)
            : undefined) ||
        printableError?.message ||
        t("unexpectedErrorOccurred")
    );
};

export const printCheckoutReceipt = async ({printMethod, checkoutId, printPayload, allowBluetoothDeviceSelection = true, allowUsbDeviceSelection = true }: PrintCheckoutReceiptParams): Promise<CashBoxCheckoutPrintResult> => {
    const targetCheckoutId = checkoutId ?? null;

    if (!targetCheckoutId) {
        return {
            success: false,
            skipped: true,
            reason: "missing_checkout",
        };
    }

    try {
        if (printMethod === "external") {
            const {
                data: respData,
                error: respError,
            } = await sendAxiosRequestWithRetry<CashBoxActionResponse>({
                method: "post",
                url: "/order/print_checkout_on_external_pay_devices",
                data: {
                    checkout_id: targetCheckoutId,
                },
            });

            if (respError || !respData) {
                return {
                    success: false,
                    error: respError,
                };
            }

            return {
                success: true,
            };
        }
        else if (printMethod === "bluetooth") {
            if (!printPayload) {
                return {
                    success: false,
                    skipped: true,
                    reason: "missing_bluetooth_payload",
                };
            }

            const printer = new ThermalPrinter(new WebSerialPort(), new EscPosCompiler(), getPrinterConfig(printPayload.printerSize ?? '58mm'));
            await printer.connect();

            const canvas = await renderReceiptCanvas(printPayload);

            await printer.print(canvas);
        }
        else if (printMethod === "usb") {
            if (!printPayload) {
                return {
                    success: false,
                    skipped: true,
                    reason: "missing_usb_payload",
                };
            }

            const printer = new ThermalPrinter(new WebUSBPort(), new EscPosCompiler(), getPrinterConfig(printPayload.printerSize ?? '58mm'));
            await printer.connect();

            const canvas = await renderReceiptCanvas(printPayload);

            await printer.print(canvas);
        }

        return {
            success: false,
            skipped: true,
            reason: "printer_not_configured",
        };
    } catch (error) {
        console.error(error);
        return {
            success: false,
            error,
        };
    }
};

export function getPrinterConfig(printerSize: string): PrinterConfig {
    if (printerSize === '58mm') {
        return {
            maxBandHeight: 128,          // کوچک نگه داشتن حلقه‌های رستر برای جلوگیری از اشباع رم پرینتر
            chunkSize: 512,              // تزریق میکروسکوپی داده به بافر USB
            chunkDelayMs: 25,            // ترمز ترمودینامیکی: اجبار سخت‌افزار به توقف برای ایجاد سوختگی عمیق‌تر روی کاغذ
            baudRate: 9600,              // بی‌تاثیر در WebUSB (صرفاً برای حفظ اینترفیس تایپ‌اسکریپت)
            flowControl: 'none',         // جلوگیری از خفگی درایور سیستم‌عامل به دلیل فقدان پین‌های CTS/RTS
            disableStatusPolling: true,  // جلوگیری از بن‌بست I/O در تراشه‌های فاقد پاسخ‌دهی دوطرفه
        };
    }

    if (printerSize === '80mm') {
        return {
            maxBandHeight: 256,          // بهینه‌سازی کامپایلر با باندهای رستر بزرگتر
            chunkSize: 512,              // اشباع باس USB با پکت‌های بزرگ برای بالاترین سرعت ممکن
            chunkDelayMs: 10,            // حذف تاخیر جاوااسکریپت (Microtask Pumping)
            baudRate: 115200,            // حداکثر سرعت باس (موثر در صورت استفاده از Fallback به WebSerial)
            flowControl: 'hardware',     // واگذاری مدیریت ترافیک به سیگنال‌های سخت‌افزاری کرنل/پرینتر
            disableStatusPolling: false, // فعال‌سازی ارتباط Full-Duplex برای خواندن درنگ‌حقیقی سنسورها
        };
    }

    throw new Error(`Engine Error: Unsupported printer size [${printerSize}]. Expected "58mm" or "80mm".`);
}

const estimateMaxChars = (canvasWidth: number, ratio: number, charWidth = 9.5) => {
    const colPixelWidth = (canvasWidth * ratio) - 5;
    return Math.floor(colPixelWidth / charWidth);
};

export async function renderReceiptCanvas(receiptData: CashBoxReceiptPrintPayload) {
    const CANVAS_WIDTH = receiptData.printerSize === '80mm' ? 576 : 384;
    const builder = new ReceiptBuilder(CANVAS_WIDTH);

    // نرمال‌سازی داده‌ها (Adapter Pattern)
    const normalizedItems = [
        ...(receiptData.items || []).map(i => ({
            title: i.title,
            qty: `${i.count}x`,
            price: i.total_price,
            sub: (i.additives || []).map(a => ({
                title: `+ ${a.title}`,
                qty: `${a.count}x`,
                price: a.total_price
            }))
        })),
        ...(receiptData.customItems || []).map(c => ({
            title: c.title,
            qty: `${c.count}x`,
            price: c.total_price,
            sub: (c.options || []).map(o => ({
                title: `+ ${o.option_title}`,
                qty: '',
                price: o.price !== "0.00" ? o.price : ''
            }))
        })),
        ...(receiptData.manualItems || []).map(m => ({
            title: m.title,
            qty: `${m.count}x`,
            price: m.total_price,
            sub: []
        }))
    ];

    builder.feed(1);

    if (receiptData.restaurantLogo) {
        builder
        .image(receiptData.restaurantLogo, 120, 120)
        .feed(1)
    }

    // هدر فاکتور
    builder
    .font('bold 22px Tahoma')
    .line(receiptData.restaurantName || 'Restaurant', 'center')
    .divider()
    .font('16px Tahoma')
    .row('Order ID:', (receiptData.orderId || '').toString())
    .row('Waiter:', receiptData.waiterName || '')
    .row('Date:', (receiptData.checkoutUpdatedAt || '').replace('T', ' ').substring(0, 19))
    .row('Currency:', receiptData.currency || '')
    .divider();

    // هدر جدول کالاها
    builder
    .font('bold 18px Tahoma')
    .table([
        {text: 'ITEM', align: 'left', widthRatio: 0.50},
        {text: 'QTY', align: 'center', widthRatio: 0.20},
        {text: 'PRICE', align: 'right', widthRatio: 0.30}
    ])
    .divider();

    // رندر کالاها با سیستم Text Wrapping پویا
    builder.font('16px Tahoma');
    const maxItemChars = estimateMaxChars(CANVAS_WIDTH, 0.50);

    for (const item of normalizedItems) {
        const title = item.title ?? '';

        builder.table([
            {text: title.substring(0, maxItemChars), align: 'left', widthRatio: 0.50},
            {text: item.qty, align: 'center', widthRatio: 0.20},
            {text: item.price, align: 'right', widthRatio: 0.30}
        ]);

        if (title.length > maxItemChars) {
            for (let i = maxItemChars; i < title.length; i += maxItemChars) {
                builder.table([
                    {text: title.substring(i, i + maxItemChars), align: 'left', widthRatio: 0.50},
                    {text: '', align: 'center', widthRatio: 0.20},
                    {text: '', align: 'right', widthRatio: 0.30}
                ]);
            }
        }

        if (item.sub.length > 0) {
            builder.font('14px Tahoma');
            const maxSubChars = estimateMaxChars(CANVAS_WIDTH, 0.50, 8.2);

            for (const subItem of item.sub) {
                builder.table([
                    {text: subItem.title.substring(0, maxSubChars), align: 'left', widthRatio: 0.50},
                    {text: subItem.qty, align: 'center', widthRatio: 0.20},
                    {text: subItem.price, align: 'right', widthRatio: 0.30}
                ]);

                if (subItem.title.length > maxSubChars) {
                    for (let i = maxSubChars; i < subItem.title.length; i += maxSubChars) {
                        builder.table([
                            {text: `  ${subItem.title.substring(i, i + maxSubChars)}`, align: 'left', widthRatio: 0.50},
                            {text: '', align: 'center', widthRatio: 0.20},
                            {text: '', align: 'right', widthRatio: 0.30}
                        ]);
                    }
                }
            }
            builder.font('16px Tahoma');
        }
    }

    // تزریق ردیف هزینه ارسال (جلوگیری از مغایرت مالی)
    if (receiptData.deliveryFee) {
        builder.table([
            {text: '* Delivery Fee', align: 'left', widthRatio: 0.50},
            {text: '1x', align: 'center', widthRatio: 0.20},
            {text: String(receiptData.deliveryFee), align: 'right', widthRatio: 0.30}
        ]);
    }


    // بخش جمع کل و امور مالی
    builder
    .divider()
    .font('18px Tahoma')
    .row('Subtotal:', String(receiptData.ordersPrice || '0.00'))
    .font('18px Tahoma')
    .row('Discount:', String(receiptData.discountPrice || '0.00'))
    .row('Tip:', String(receiptData.tipPrice || '0.00'))
    .font('bold 18px Tahoma')
    .row('Total Paid:', String(receiptData.paidPrice || '0.00'))
    .divider();

    // جدول مالیات (VAT)
    if (receiptData.taxes && receiptData.taxes.length > 0) {
        builder
        .font('bold 18px Tahoma')
        .table([
            {text: 'VAT', align: 'left', widthRatio: 0.20},
            {text: 'NET', align: 'center', widthRatio: 0.25},
            {text: 'TAX', align: 'center', widthRatio: 0.25},
            {text: 'GROSS', align: 'right', widthRatio: 0.30}
        ])
        .font('16px Tahoma');

        for (const tax of receiptData.taxes) {
            builder.table([
                {text: `${tax.vat}%`, align: 'left', widthRatio: 0.20},
                {text: tax.net, align: 'center', widthRatio: 0.25},
                {text: tax.tax, align: 'center', widthRatio: 0.25},
                {text: tax.gross, align: 'right', widthRatio: 0.30}
            ]);
        }
        builder.divider();
    }

    // امضای دیجیتال و کد QR
    if (receiptData.qrBase64) {
        const qrSize = 180;

        builder
        .image(receiptData.qrBase64, qrSize, qrSize)
        .font('14px monospace')
        .line(receiptData.qrSignatureText || '', 'center');
    }

    // تغذیه نهایی کاغذ برای برش ایمن
    builder.feed(3);

    // تولید خروجی
    return await builder.compile();
}
